import { Link, Typography } from '@mui/material'
import PrimaryButton from '@/components/common/PrimaryButton'
import { useEffect, useRef, useState } from 'react'
import { buildPublicCustomerOrderDetailPath } from '@/services/public-storefront-paths'
import API from '@/utils/api'
import { updateProcessState } from '@/slices/process-slice'
import { useDispatch } from 'react-redux'
import { OrderStatus, parseOrderStatus } from '@/types/enums/OrderStatus'
import { postPaymentCheckoutSession } from '@/services/payment-session'
import { useTenantBranding } from '@/hooks/use-tenant-branding'
import { primaryActionSx } from '@/theme/primary-action'
import { tenantActionCtaSx } from '@/theme/tenant-cta-sx'

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 15 * 60 * 1000
const TERMINAL_FAILURE_STATUS = /^(Failed|Cancelled|Expired|Voided)$/i

export interface PaymentCheckoutData {
  /** For polling and postMessage matching */
  order_id: string
  /** Uses `API_PATHS.PAYMENT_CHECKOUT` via payment session service */
  billing_snapshot_id: string
}

interface PaymentCheckoutProps {
  data?: PaymentCheckoutData
  callback?: () => void
  resetTrigger?: number
}

function PaymentCheckout(props: PaymentCheckoutProps) {
  const { data, callback, resetTrigger } = props
  const dispatch = useDispatch()
  const { primaryTokens: payActionTokens } = useTenantBranding()
  const payCtaSx = payActionTokens
    ? tenantActionCtaSx(payActionTokens)
    : primaryActionSx

  const [bankUrl, setBankUrl] = useState<string | null>(null)
  const [fallbackState, setFallbackState] = useState<'none' | 'waiting'>('none')
  const paymentWindowRef = useRef<Window | null>(null)
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const popupBlockedRef = useRef(false)
  const handledMessageRef = useRef(false)
  const awaitingOrderIdRef = useRef<string | null>(null)

  const checkout = async () => {
    popupBlockedRef.current = false
    handledMessageRef.current = false
    const orderId = `${data?.order_id ?? ''}`.trim() || null
    const snapshot = `${data?.billing_snapshot_id ?? ''}`.trim() || null
    if (!orderId || !snapshot) {
      alert('Payment cannot start: order is missing a billing snapshot. Refresh the page or contact support.')
      return
    }
    awaitingOrderIdRef.current = orderId
    setFallbackState('none')

    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    let url: string
    try {
      const { redirectUrl } = await postPaymentCheckoutSession({ billingSnapshotId: snapshot })
      url = redirectUrl
    } catch {
      alert('Something went wrong while starting payment.')
      return
    }

    if (!url) {
      alert('Payment session could not be started. Please try again.')
      return
    }

    let popup = paymentWindowRef.current
    if (!popup || popup.closed) {
      popup = window.open('', 'paymentWindow', 'popup,width=480,height=720,resizable=yes,scrollbars=yes')
    }

    if (!popup) {
      popupBlockedRef.current = true
      window.location.assign(url)
      return
    }

    paymentWindowRef.current = popup

    try {
      popup.document.title = 'Redirecting to payment…'
      popup.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
      popup.document.body.style.padding = '24px'
      popup.document.body.innerHTML = `<h2 style="margin:0 0 8px 0;">Redirecting to payment…</h2><p style="margin:0;color:#555;">Please don't close this window.</p>`
    } catch {
      /* ignore cross-origin / document access errors */
    }

    try {
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.location.href = url
      }
    } catch {
      /* ignore */
    }
    setBankUrl(url)
  }

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const d = event.data as { type?: string; order_id?: string } | null
      if (!d || d.type !== 'GMF_PAYMENT_CALLBACK') return
      if (!awaitingOrderIdRef.current && !bankUrl) return
      if (awaitingOrderIdRef.current && d.order_id && d.order_id !== awaitingOrderIdRef.current) return
      if (handledMessageRef.current) return
      handledMessageRef.current = true

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      try {
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.close()
        }
      } catch {
        /* ignore */
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }

      dispatch(updateProcessState(false))
      setBankUrl(null)
      setFallbackState('none')
      awaitingOrderIdRef.current = null
      callback?.()
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [dispatch, callback, bankUrl])

  useEffect(() => {
    if (!bankUrl) return

    dispatch(updateProcessState(true))
    const paymentPage = paymentWindowRef.current

    if (paymentPage && !paymentPage.closed) {
      intervalIdRef.current = setInterval(() => {
        if (!paymentPage.closed) return
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        dispatch(updateProcessState(false))

        if (handledMessageRef.current) return

        const oid = awaitingOrderIdRef.current
        if (!oid) {
          setBankUrl(null)
          awaitingOrderIdRef.current = null
          callback?.()
          return
        }

        setFallbackState('waiting')
        const startedAt = Date.now()

        pollIntervalRef.current = setInterval(() => {
          if (handledMessageRef.current) return

          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current)
              pollIntervalRef.current = null
            }
            setFallbackState('none')
            setBankUrl(null)
            awaitingOrderIdRef.current = null
            callback?.()
            return
          }

          // Tenant-scoped storefront read. A 404 falls into the existing catch and retries on the
          // next tick, bounded by POLL_TIMEOUT_MS — unchanged behaviour for any other failure.
          API.get(`/${buildPublicCustomerOrderDetailPath(oid)}`, {
            skipErrorToast: true,
          })
            .then((res) => {
              if (handledMessageRef.current) return
              if (res.data?.resultCode !== 200 || !res.data?.success) return
              const order = res.data?.data
              if (!order) return

              const paid = order.is_paid === true
              const ps = (order.order_payment_details?.payment_status || order.payment_status || '')
              const terminalFailure = TERMINAL_FAILURE_STATUS.test(ps)
              const orderStatus = parseOrderStatus(order.order_status)
              const orderTerminal =
                orderStatus === OrderStatus.Cancelled ||
                orderStatus === OrderStatus.Refunded ||
                orderStatus === OrderStatus.Returned

              if (paid || terminalFailure || orderTerminal) {
                if (pollIntervalRef.current) {
                  clearInterval(pollIntervalRef.current)
                  pollIntervalRef.current = null
                }
                handledMessageRef.current = true
                setFallbackState('none')
                setBankUrl(null)
                awaitingOrderIdRef.current = null
                callback?.()
              }
            })
            .catch(() => {
              /* ignore; retry on next tick */
            })
        }, POLL_INTERVAL_MS)
      }, 500)

      window.onbeforeunload = () => 'Changes made will not be saved'
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
      window.onbeforeunload = null
    }
  }, [bankUrl, dispatch, callback])

  useEffect(() => {
    if (resetTrigger == null) return
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    setBankUrl(null)
    setFallbackState('none')
    awaitingOrderIdRef.current = null
    dispatch(updateProcessState(false))
  }, [resetTrigger, dispatch])

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [])

  if (fallbackState === 'waiting' && bankUrl) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
        Waiting for payment confirmation…
        <br />
        <Link
          component="button"
          variant="body2"
          onClick={() => window.location.assign(bankUrl)}
          sx={{ mt: 0.5, cursor: 'pointer' }}
        >
          Continue in this window
        </Link>
      </Typography>
    )
  }

  const canPay =
    !!data?.order_id?.toString().trim() && !!data?.billing_snapshot_id?.toString().trim()

  return (
    <PrimaryButton
      onClick={(e) => {
        e.preventDefault()
        void checkout()
      }}
      disabled={!!bankUrl || !canPay}
      fullWidth
      size="large"
      data-tenant-primary={payActionTokens?.fill}
      sx={payCtaSx}
    >
      Make Payment
    </PrimaryButton>
  )
}

export default PaymentCheckout
